import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  Spinner,
  Typography,
} from "@material-tailwind/react";

import AlertMessage from "../../../common/actions/AlertMessage";
import Loader from "../../../common/loader/Loader";
import SectionTitle from "../../../common/shared/SectionTitle";
import {
  getSingleSurvey,
  submitSurveyResponse,
} from "../../../redux/actions/survey_action";

const links = [
  { name: "Surveys & Polls", link: "/home/surveys" },
  { name: "Survey Detail" },
];

const normalizeOption = (option) => {
  if (typeof option === "string") {
    return { label: option, value: option };
  }

  return {
    label: option?.label || option?.name || option?.value || "Option",
    value: option?.value || option?.label || option?.name || "Option",
  };
};

function SurveyDetail() {
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [answersMap, setAnswersMap] = useState({});

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentSurvey, response, surveyError, message } = useSelector(
    (state) => state.survey,
  );

  useEffect(() => {
    dispatch(getSingleSurvey(id)).finally(() => setLoading(false));
  }, [dispatch, id]);

  useEffect(() => {
    if (!currentSurvey?.questions) return;

    if (response?.answers?.length) {
      const nextValues = response.answers.reduce((acc, item) => {
        acc[item.question_id] = item.answer;
        return acc;
      }, {});
      setAnswersMap(nextValues);
      return;
    }

    const emptyValues = currentSurvey.questions.reduce((acc, question) => {
      acc[question.id] = question.type === "multiple_choice" ? [] : "";
      return acc;
    }, {});

    setAnswersMap(emptyValues);
  }, [currentSurvey, response]);

  const alreadySubmitted = useMemo(() => !!response?.id, [response]);

  const updateAnswer = (questionId, value) => {
    setAnswersMap((prev) => ({ ...prev, [questionId]: value }));
  };

  const handleMultipleChoice = (questionId, optionValue) => {
    const currentValues = Array.isArray(answersMap[questionId])
      ? answersMap[questionId]
      : [];

    const nextValues = currentValues.includes(optionValue)
      ? currentValues.filter((item) => item !== optionValue)
      : [...currentValues, optionValue];

    updateAnswer(questionId, nextValues);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitLoading(true);

    const answers = (currentSurvey?.questions || []).map((question) => ({
      question_id: question.id,
      answer: answersMap[question.id],
    }));

    const submittedResponse = await dispatch(
      submitSurveyResponse(id, { answers }),
    );
    setSubmitLoading(false);

    if (submittedResponse?.id) {
      navigate("/home/surveys", {
        replace: true,
        state: { surveySuccess: "Survey response submitted successfully." },
      });
      return;
    }

    await dispatch(getSingleSurvey(id));
  };

  const renderAnswerValue = (value) => {
    if (Array.isArray(value)) {
      return value.length ? value.join(", ") : "No answer provided";
    }

    return value || "No answer provided";
  };

  return (
    <>
      <SectionTitle
        title={currentSurvey?.title || "Survey Detail"}
        description={
          currentSurvey?.description ||
          "Review the questions below and submit your response."
        }
        children={links}
        link="/home/surveys"
      />

      <div className="my-8 max-w-[1200px] m-auto px-4">
        {surveyError && <AlertMessage type="error" message={surveyError} />}
        {message && <AlertMessage type="success" message={message} />}

        {loading ? (
          <Loader />
        ) : (
          <Card>
            <CardBody>
              {alreadySubmitted && (
                <AlertMessage
                  type="success"
                  message="Your response has already been recorded for this survey."
                />
              )}

              <form onSubmit={handleSubmit} className="space-y-8">
                {(currentSurvey?.questions || []).map((question, index) => {
                  const options = (question.options || []).map(normalizeOption);

                  return (
                    <div
                      key={question.id}
                      className="border-b border-blue-gray-50 pb-8"
                    >
                      <Typography variant="h6" color="blue-gray">
                        {index + 1}. {question.prompt}
                      </Typography>

                      {(question.type === "text" ||
                        question.type === "textarea") && (
                        <textarea
                          className="mt-4 w-full rounded-lg border border-blue-gray-100 p-4 outline-none"
                          rows={question.type === "textarea" ? 5 : 3}
                          value={answersMap[question.id] || ""}
                          onChange={(e) =>
                            updateAnswer(question.id, e.target.value)
                          }
                          disabled={alreadySubmitted}
                          required={question.required !== false}
                        />
                      )}

                      {question.type === "single_choice" && (
                        <div className="mt-4 space-y-3">
                          {options.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center gap-3 text-sm text-blue-gray-700"
                            >
                              <input
                                type="radio"
                                name={question.id}
                                value={option.value}
                                checked={
                                  answersMap[question.id] === option.value
                                }
                                onChange={(e) =>
                                  updateAnswer(question.id, e.target.value)
                                }
                                disabled={alreadySubmitted}
                                required={question.required !== false}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {question.type === "multiple_choice" && (
                        <div className="mt-4 space-y-3">
                          {options.map((option) => (
                            <label
                              key={option.value}
                              className="flex items-center gap-3 text-sm text-blue-gray-700"
                            >
                              <input
                                type="checkbox"
                                value={option.value}
                                checked={(
                                  answersMap[question.id] || []
                                ).includes(option.value)}
                                onChange={() =>
                                  handleMultipleChoice(
                                    question.id,
                                    option.value,
                                  )
                                }
                                disabled={alreadySubmitted}
                              />
                              <span>{option.label}</span>
                            </label>
                          ))}
                        </div>
                      )}

                      {alreadySubmitted && (
                        <Typography className="mt-4 text-sm text-blue-gray-700">
                          Submitted answer:{" "}
                          {renderAnswerValue(answersMap[question.id])}
                        </Typography>
                      )}
                    </div>
                  );
                })}

                {!alreadySubmitted && (
                  <Button type="submit" className="flex items-center gap-3">
                    {submitLoading && <Spinner className="h-4 w-4" />}
                    Submit Response
                  </Button>
                )}
              </form>
            </CardBody>
          </Card>
        )}
      </div>
    </>
  );
}

export default SurveyDetail;
